import { useMemo } from 'react';
import { themes, type GenderPreset } from '../../theme';
import { Icon } from './Icon';
import { Sheet } from '../shared/Sheet';
import { LanguageSelect } from '../shared/LanguageSelect';
import { useT } from '../../i18n';
import { EncryptionSettingsCard } from '../../crypto/EncryptionSettingsCard';
import { NotificationSettings } from './NotificationSettings';
import { useMe, useUpdateProfile, useExcludedCategories } from '../../data/queries';
import type { Baby } from '../../data/types';

interface Props {
  open: boolean;
  gender: GenderPreset;
  setGender: (g: GenderPreset) => void;
  baby: Baby;
  onClose: () => void;
}

function detectDefaultUnits(): 'metric' | 'imperial' {
  try {
    return navigator.language === 'en-US' ? 'imperial' : 'metric';
  } catch {
    return 'metric';
  }
}

const ALL_CATEGORIES = [
  'Boundary Stone',
  'Cognitive',
  'Communication',
  'Language',
  'Motor - Fine',
  'Motor - Gross',
  'Nutrition',
  'Self-Care',
  'Sleep',
  'Social & Emotional',
  'Teeth',
] as const;

export function SettingsSheet({
  open,
  gender,
  setGender,
  baby,
  onClose,
}: Props) {
  const me = useMe();
  const updateProfile = useUpdateProfile();
  const t = useT();
  const [excluded, toggleCategory] = useExcludedCategories(baby.id ?? null);

  const preferredUnits = me.data?.profile?.preferred_units ?? detectDefaultUnits();

  return (
    <Sheet open={open} onClose={onClose} size="lg" maxHeight="92dvh" label={t('settings.title', 'Settings')}>
      <div className="flex flex-col gap-7 overflow-y-auto px-6 pb-8 pt-0">
        {/* Close-row */}
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('settings.title', 'Settings')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close settings')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        {/* Milestone Categories Preferences */}
        <section>
          <h2 className="font-serif text-xl tracking-tight text-ink mb-1">
            {t('settings.categories.title', 'Milestone Categories')}
          </h2>
          <p className="text-[13px] text-ink-2 leading-relaxed mb-4">
            {t('settings.categories.desc', 'Choose which development categories you want to track. Deselected categories will be hidden from the UI, statistics, leaps, and lists.')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ALL_CATEGORIES.map((cat) => {
              const isExcluded = excluded.includes(cat);
              const label = t('milestones.group.' + cat, cat);
              return (
                <button
                  key={cat}
                  onClick={() => toggleCategory(cat)}
                  className="flex min-h-12 w-full items-center justify-between rounded-xl border border-line bg-surface px-4 py-2.5 text-left transition-all active:scale-[0.99] hover:bg-grey-soft/5"
                >
                  <span className="text-[14px] font-medium text-ink-2 max-w-[80%] truncate">{label}</span>
                  <div
                    className={[
                      'flex size-5.5 items-center justify-center rounded-md border transition-all',
                      isExcluded
                        ? 'border-line bg-transparent'
                        : 'border-accent bg-accent text-white',
                    ].join(' ')}
                  >
                    {!isExcluded && <Icon name="check" size={14} color="var(--color-bg)" />}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Notifications (per baby: allow + frequency) */}
        <NotificationSettings />

        {/* Photo privacy / encryption */}
        <EncryptionSettingsCard />

        {/* Theme accent */}
        <section>
          <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">{t('profile.theme', 'Theme accent')}</h2>
          <div className="flex gap-2">
            {(Object.keys(themes) as GenderPreset[]).map((g) => {
              const a = themes[g].accent;
              const on = gender === g;
              return (
                <button
                  key={g}
                  onClick={() => setGender(g)}
                  className={[
                    'flex flex-1 min-h-14 flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 text-[12.5px] transition-all',
                    on ? 'font-semibold text-ink' : 'font-medium text-ink-2',
                  ].join(' ')}
                  style={
                    on
                      ? {
                          border: `1.5px solid ${a}`,
                          background: `color-mix(in oklch, ${a} 10%, var(--color-bg))`,
                        }
                      : {
                          border: '1px solid var(--color-line)',
                          background: 'var(--color-surface)',
                        }
                  }
                >
                  <span className="size-4.5 rounded-full" style={{ background: a }} />
                  {themes[g].name}
                </button>
              );
            })}
          </div>
        </section>

        {/* Language preference */}
        <section>
          <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">{t('language.title', 'Language')}</h2>
          <div className="flex items-center justify-between rounded-xl border border-line bg-surface px-4 py-3.5">
            <span className="text-[14.5px] text-ink">{t('language.subtitle', 'Content & app language')}</span>
            <LanguageSelect />
          </div>
        </section>

        {/* Units preference */}
        <section>
          <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">{t('profile.units', 'Units')}</h2>
          <div className="overflow-hidden rounded-xl border border-line bg-surface">
            <div className="flex items-center justify-between px-4 py-3.5">
              <span className="text-[14.5px] text-ink">{t('profile.weightHeight', 'Weight & height')}</span>
              <div className="flex rounded-lg bg-grey-soft p-0.5">
                {(['metric', 'imperial'] as const).map((u) => {
                  const on = preferredUnits === u;
                  return (
                    <button
                      key={u}
                      onClick={() =>
                        updateProfile.mutate(
                          { preferred_units: u },
                          { onError: (e) => console.error(e) },
                        )
                      }
                      disabled={updateProfile.isPending}
                      className={[
                        'inline-flex items-center px-4 py-1.5 text-[12.5px] rounded-md transition-all',
                        on
                          ? 'bg-surface text-ink font-semibold shadow-[0_1px_2px_rgba(33,28,21,0.08)]'
                          : 'bg-transparent text-ink-2 font-medium',
                      ].join(' ')}
                    >
                      {u === 'metric' ? 'kg · cm' : 'lb · in'}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="border-t border-hairline px-4 py-3.5">
              <span className="text-[14.5px] text-ink">{t('profile.headCirc', 'Head circumference')}</span>
              <span className="ml-2 text-[13px] text-ink-3">{t('profile.alwaysCm', 'always cm')}</span>
            </div>
          </div>
        </section>

        {/* Legal documents: what's currently accepted + links to the full text */}
        <section>
          <h2 className="mb-3 font-serif text-xl tracking-tight text-ink">{t('settings.legal.title', 'Legal')}</h2>
          <div className="overflow-hidden rounded-xl border border-line bg-surface">
            {(me.data?.legal ?? []).map((entry, i) => (
              <a
                key={entry.type}
                href={`https://babynook.app/${entry.type}`}
                target="_blank"
                rel="noopener noreferrer"
                className={[
                  'flex items-center justify-between px-4 py-3.5 hover:bg-tint/10 transition-colors',
                  i > 0 ? 'border-t border-hairline' : '',
                ].join(' ')}
              >
                <span className="text-[14.5px] text-ink">
                  {entry.type === 'terms'
                    ? t('settings.legal.terms', 'Terms and Conditions')
                    : t('settings.legal.privacy', 'Privacy Policy')}
                </span>
                <span className="text-[12.5px] text-ink-3">
                  {entry.accepted_version
                    ? t('settings.legal.accepted', 'Accepted {version}').replace('{version}', entry.accepted_version)
                    : t('settings.legal.notAccepted', 'Not yet accepted')}
                </span>
              </a>
            ))}
          </div>
        </section>
      </div>
    </Sheet>
  );
}
